import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  X, ChevronLeft, ChevronRight, RotateCw, ZoomIn, ZoomOut,
  Download, Trash2, FlipHorizontal, Ruler, Pencil, Save,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import type { EnrichedToothImage, ImageAdjustments, ImageAnnotation, ImageMeasurement } from '@/hooks/useToothImages';
import {
  useDeleteToothImage,
  useUpdateToothImageAdjustments,
  useUpdateToothImageAnnotations,
  useUpdateToothImageMeasurements,
  buildCssFilter,
} from '@/hooks/useToothImages';
import { MeasurementTool } from './MeasurementTool';
import { AnnotationTool } from './AnnotationTool';
import { Button, ButtonAnchor } from '@/components/ui/Button';

// ── Helpers ────────────────────────────────────────────────────────────────────

function nextRotation(r: 0 | 90 | 180 | 270): 0 | 90 | 180 | 270 {
  return ((r + 90) % 360) as 0 | 90 | 180 | 270;
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  defaultValue,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs text-ds-muted">
        <span>{label}</span>
        <button
          onClick={() => onChange(defaultValue)}
          className="text-ds-muted hover:text-ds-placeholder"
          title="Reset"
        >
          {value.toFixed(1)}
        </button>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer accent-ds-teal"
      />
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ImageLightboxProps {
  images: EnrichedToothImage[];
  initialIndex: number;
  patientId: string;
  onClose: () => void;
  userId: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ImageLightbox({
  images,
  initialIndex,
  patientId,
  onClose,
  userId,
}: ImageLightboxProps) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const image = images[idx];

  // Local mutable copies — saved only on explicit button click
  const [adjustments, setAdjustments] = useState<ImageAdjustments>(image?.adjustments ?? {});
  const [annotations, setAnnotations] = useState<ImageAnnotation[]>(image?.annotations ?? []);
  const [measurements, setMeasurements] = useState<ImageMeasurement[]>(image?.measurements ?? []);

  const [measureActive, setMeasureActive] = useState(false);
  const [annotateActive, setAnnotateActive] = useState(false);

  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 1000, h: 800 });

  // Reset local state when image changes
  useEffect(() => {
    if (!image) return;
    setAdjustments(image.adjustments ?? {});
    setAnnotations(image.annotations ?? []);
    setMeasurements(image.measurements ?? []);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setMeasureActive(false);
    setAnnotateActive(false);
  }, [image?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteImage = useDeleteToothImage();
  const updateAdjustments = useUpdateToothImageAdjustments();
  const updateAnnotations = useUpdateToothImageAnnotations();
  const updateMeasurements = useUpdateToothImageMeasurements();

  // ── Navigation ─────────────────────────────────────────────────────────────
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIdx((i) => Math.min(images.length - 1, i + 1)), [images.length]);

  // ── Keyboard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(4, z + 0.25));
      if (e.key === '-') setZoom((z) => Math.max(0.5, z - 0.25));
      if (e.key === 'r' || e.key === 'R')
        setAdjustments((a) => ({ ...a, rotation_deg: nextRotation((a.rotation_deg ?? 0) as 0 | 90 | 180 | 270) }));
      if (e.key === 'i' || e.key === 'I')
        setAdjustments((a) => ({ ...a, invert: !a.invert }));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  // ── Wheel zoom ─────────────────────────────────────────────────────────────
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => Math.max(0.5, Math.min(4, z - e.deltaY * 0.001)));
  };

  // ── Pan ────────────────────────────────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1 || measureActive || annotateActive) return;
    setIsPanning(true);
    panStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning) return;
    setPan({
      x: panStart.current.px + (e.clientX - panStart.current.mx),
      y: panStart.current.py + (e.clientY - panStart.current.my),
    });
  };

  const handleMouseUp = () => setIsPanning(false);

  // ── Adjustments helpers ────────────────────────────────────────────────────
  const adj = adjustments;
  const br = adj.brightness ?? 1;
  const co = adj.contrast ?? 1;
  const inv = adj.invert ?? false;
  const rot = adj.rotation_deg ?? 0;

  const cssFilter = buildCssFilter(adj);
  const cssTransform = `rotate(${rot}deg) scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`;

  // ── Save handlers ──────────────────────────────────────────────────────────
  const handleSaveAdjustments = async () => {
    try {
      await updateAdjustments.mutateAsync({ imageId: image.id, patientId, adjustments });
      toast.success(t('dental.imaging.lightbox.saveAdjustments'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleSaveAnnotations = async () => {
    try {
      await updateAnnotations.mutateAsync({ imageId: image.id, patientId, annotations });
      await updateMeasurements.mutateAsync({ imageId: image.id, patientId, measurements });
      toast.success(t('dental.imaging.lightbox.saveAnnotations'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('dental.imaging.lightbox.deleteConfirm'))) return;
    try {
      await deleteImage.mutateAsync({ imageId: image.id, patientId });
      if (images.length === 1) {
        onClose();
      } else {
        setIdx((i) => Math.max(0, i - 1));
      }
      toast.success(t('dental.imaging.lightbox.delete'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  if (!image) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex bg-black/95"
      role="dialog"
      aria-modal="true"
    >
      {/* ── Left: image area ─────────────────────────────────────────────── */}
      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: isPanning ? 'grabbing' : zoom > 1 ? 'grab' : 'default' }}
      >
        {/* Image */}
        <div className="relative" style={{ transform: cssTransform, transition: isPanning ? 'none' : 'transform 0.1s' }}>
          <img
            src={image.signedUrl ?? ''}
            alt={image.document_original_filename ?? 'tooth image'}
            style={{ filter: cssFilter, maxHeight: '90vh', maxWidth: '100%', objectFit: 'contain' }}
            onLoad={(e) => {
              const img = e.currentTarget;
              setImgNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
            }}
            draggable={false}
          />

          {/* Measurement SVG overlay */}
          <div className="absolute inset-0">
            <MeasurementTool
              measurements={measurements}
              onChange={setMeasurements}
              imageWidth={imgNaturalSize.w}
              imageHeight={imgNaturalSize.h}
              active={measureActive}
              onToggleActive={() => {
                setMeasureActive((v) => !v);
                setAnnotateActive(false);
              }}
            />
          </div>

          {/* Annotation SVG overlay */}
          <div className="absolute inset-0">
            <AnnotationTool
              annotations={annotations}
              onChange={setAnnotations}
              imageWidth={imgNaturalSize.w}
              imageHeight={imgNaturalSize.h}
              active={annotateActive}
              onToggleActive={() => {
                setAnnotateActive((v) => !v);
                setMeasureActive(false);
              }}
              userId={userId}
            />
          </div>
        </div>

        {/* Navigation arrows */}
        {idx > 0 && (
          <button
            onClick={prev}
            className="absolute left-3 rounded-full bg-ds-scrim p-2 text-white hover:bg-black/80"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {idx < images.length - 1 && (
          <button
            onClick={next}
            className="absolute right-[376px] rounded-full bg-ds-scrim p-2 text-white hover:bg-black/80"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}

        {/* Close */}
        <Button
          type="button"
          variant="tertiary"
          size="icon"
          onClick={onClose}
          className="absolute right-[376px] top-4"
          aria-label={t('common.close')}
        >
          <X />
        </Button>
      </div>

      {/* ── Right: controls panel ────────────────────────────────────────── */}
      <div className="flex w-[360px] flex-shrink-0 flex-col gap-4 overflow-y-auto border-l border-slate-800 bg-slate-950 p-4">

        {/* Meta */}
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
          <p className="text-sm font-semibold text-white">
            {t(`dental.imaging.kinds.${image.image_kind}`)}
          </p>
          <p className="mt-0.5 text-xs text-ds-muted">
            {new Date(image.acquired_at).toLocaleDateString('da-DK', {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </p>
          {image.tooth_fdis.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {image.tooth_fdis.map((fdi) => (
                <span key={fdi} className="rounded-full bg-slate-800 px-2 py-0.5 text-xs text-ds-placeholder">
                  {fdi}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Brightness */}
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-3 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
            {t('dental.imaging.lightbox.brightness')} / {t('dental.imaging.lightbox.contrast')}
          </p>
          <SliderRow
            label={t('dental.imaging.lightbox.brightness')}
            value={br}
            min={0} max={2} step={0.05} defaultValue={1}
            onChange={(v) => setAdjustments((a) => ({ ...a, brightness: v }))}
          />
          <SliderRow
            label={t('dental.imaging.lightbox.contrast')}
            value={co}
            min={0} max={2} step={0.05} defaultValue={1}
            onChange={(v) => setAdjustments((a) => ({ ...a, contrast: v }))}
          />
          <SliderRow
            label={t('dental.imaging.lightbox.gamma')}
            value={adj.gamma ?? 1}
            min={0} max={2} step={0.05} defaultValue={1}
            onChange={(v) => setAdjustments((a) => ({ ...a, gamma: v }))}
          />
          <SliderRow
            label={`${t('dental.imaging.lightbox.sharpen')} (TODO)`}
            value={adj.sharpen ?? 0}
            min={0} max={1} step={0.05} defaultValue={0}
            onChange={(v) => setAdjustments((a) => ({ ...a, sharpen: v }))}
          />
        </div>

        {/* Toggles */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setAdjustments((a) => ({ ...a, invert: !inv }))}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              inv
                ? 'bg-ds-teal text-ds-on-accent'
                : 'bg-slate-800 text-ds-placeholder hover:bg-slate-700',
            )}
          >
            <FlipHorizontal className="h-3.5 w-3.5" />
            {t('dental.imaging.lightbox.invert')}
          </button>

          <button
            onClick={() => setAdjustments((a) => ({ ...a, rotation_deg: nextRotation((a.rotation_deg ?? 0) as 0 | 90 | 180 | 270) }))}
            className="flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-ds-placeholder hover:bg-slate-700"
          >
            <RotateCw className="h-3.5 w-3.5" />
            {t('dental.imaging.lightbox.rotate')} ({rot}°)
          </button>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
            className="rounded-lg bg-slate-800 p-2 text-ds-placeholder hover:bg-slate-700"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <div className="flex-1 text-center text-xs text-ds-muted">
            {Math.round(zoom * 100)}%
          </div>
          <button
            onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
            className="rounded-lg bg-slate-800 p-2 text-ds-placeholder hover:bg-slate-700"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>

        {/* Tools row */}
        <div className="flex gap-2">
          {/* MeasurementTool toggle rendered inline */}
          <button
            onClick={() => { setMeasureActive((v) => !v); setAnnotateActive(false); }}
            title={t('dental.imaging.lightbox.zoom')}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              measureActive ? 'bg-ds-amber text-ds-text' : 'bg-slate-800 text-ds-placeholder hover:bg-slate-700',
            )}
          >
            <Ruler className="h-3.5 w-3.5" />
            Måling
          </button>
          <button
            onClick={() => { setAnnotateActive((v) => !v); setMeasureActive(false); }}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              annotateActive ? 'bg-ds-teal text-ds-on-accent' : 'bg-slate-800 text-ds-placeholder hover:bg-slate-700',
            )}
          >
            <Pencil className="h-3.5 w-3.5" />
            Annoter
          </button>
        </div>

        {/* Save */}
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleSaveAdjustments}
            disabled={updateAdjustments.isPending}
            size="full"
          >
            <Save />
            {t('dental.imaging.lightbox.saveAdjustments')}
          </Button>

          {(annotations.length > 0 || measurements.length > 0) && (
            <Button
              onClick={handleSaveAnnotations}
              disabled={updateAnnotations.isPending || updateMeasurements.isPending}
              variant="secondary"
              size="full"
            >
              <Save />
              {t('dental.imaging.lightbox.saveAnnotations')}
            </Button>
          )}
        </div>

        {/* Download / Delete */}
        <div className="mt-auto flex gap-2">
          {image.signedUrl && (
            <ButtonAnchor
              href={image.signedUrl}
              download={image.document_original_filename ?? 'image'}
              variant="secondary"
              className="flex-1"
            >
              <Download />
              {t('dental.imaging.lightbox.download')}
            </ButtonAnchor>
          )}
          <Button
            onClick={handleDelete}
            disabled={deleteImage.isPending}
            variant="danger"
            className="flex-1"
          >
            <Trash2 />
            {t('dental.imaging.lightbox.delete')}
          </Button>
        </div>

        {/* Keyboard hint */}
        <p className="text-center text-xs text-ds-muted">
          ←/→ nav · +/− zoom · R roter · I inverter · ESC luk
        </p>
      </div>
    </div>,
    document.body,
  );
}
